import { pickBy } from 'lodash-es';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { identifyAnalyticsUser, logAnalyticsPageView } from '@/Analytics/actions';
import type {
  IIdentifyUserProps,
  ILogPageViewProps,
  TPageViewEventProperties,
} from '@/Analytics/types';
import { usePreferredLocale } from '@/Locale';
import { isDefined } from '@/Utils';

import { useReferrerContext } from '../useReferrerContext';

/**
 * ******************************************************************
 * NOTE: This file is a duplicate of another file located at:
 * projects/home/frontend/src/ui/common/hooks/useAnalytics/useAnalytics.ts
 * Any changes made to this file should also be made to the original
 * file to maintain consistency and avoid code duplication issues.
 *
 * This duplication was done instead of a complete move to avoid breaking tests.
 * To resolve this, we would need to create higher-level mocks (at the @color/lib level)
 * and refactor many tests, which requires a significant amount of time. For more details
 * and future resolution, see the Jira ticket: https://getcolor.atlassian.net/browse/FG-63
 * ******************************************************************
 */

/**
 * Hook to provide event tracking analytics to various providers.
 */
export const useAnalytics = (): {
  dangerouslyLogPageViewAnalytics: (name: string, properties?: TPageViewEventProperties) => void;
} => {
  const referrerRef = useReferrerContext();
  const { locale } = usePreferredLocale();

  // This hook can be called to attach extra information about a page view, generic URL changes are
  // expected to call `logPageLoadAnalytics` instead. This method filters out null and undefined
  // values.
  const dangerouslyLogPageViewAnalytics = useCallback(
    (name: string, properties?: TPageViewEventProperties) => {
      const referrer = referrerRef.current;
      logAnalyticsPageView(name, {
        preferred_locale: locale,
        ...pickBy(properties, (value) => isDefined(value)),
        referrer,
      });
    },
    [referrerRef, locale]
  );

  return {
    dangerouslyLogPageViewAnalytics,
  };
};

/**
 * Logs a page view for analytics using the uniquePageId passed.
 *
 * Prerequisites for logging:
 *   `uniquePageId`, `pageName` are truthy
 *   `loggingIsEnabled` is true.
 *
 * This hook caches the uniquePageId and, once the prerequisites are met, logs
 * one time, until the uniquePageId changes.
 */
export function useLogPageViewUsingUniqueId(props: ILogPageViewProps): void {
  const { pathname } = useLocation();
  const { loggingIsEnabled = true, uniquePageId = pathname, pageName, properties } = props;
  const { dangerouslyLogPageViewAnalytics } = useAnalytics();

  const lastLoggedUniquePageIdRef = useRef('');

  /**
   * `useEffect` is often dangerous and unnecessary. This usage needs review by #ux-programs team.
   * See https://getcolor.atlassian.net/browse/UXTASK-1406
   */
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    if (
      loggingIsEnabled &&
      uniquePageId &&
      pageName &&
      lastLoggedUniquePageIdRef.current !== uniquePageId
    ) {
      lastLoggedUniquePageIdRef.current = uniquePageId;
      // nosemgrep:semgrep-rules.ui-dangerouslyLogPageViewAnalytics
      dangerouslyLogPageViewAnalytics(pageName, properties);
    }
  }, [loggingIsEnabled, dangerouslyLogPageViewAnalytics, pageName, properties, uniquePageId]);
}

export function useIdentifyAnalyticsUser(userProps: IIdentifyUserProps | undefined): void {
  /*
   * Reviewed by UX Programs as a valid `useEffect` usage. See UXTASK-1406
   */
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    if (userProps) {
      identifyAnalyticsUser(userProps);
    }
  }, [userProps]);
}
